import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { onboardingRuns } from "../../api/endpoints/onboardingRuns";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { useIsPlatformAdmin } from "../auth/useIsPlatformAdmin";
import { StatusBadge } from "./StatusBadge";
import { ArtifactDrawer } from "./ArtifactDrawer";
import { StepsTimeline } from "./StepsTimeline";
import { PreviewDiffTable } from "./PreviewDiffTable";
import { RunActions } from "./RunActions";
import { runPollInterval } from "./runPoll";
import { formatLeaseCountdown } from "./leaseCountdown";

const LEASE_STATES = new Set(["claimed", "running"]);

export function RunDetail({ runId }: { runId: string }) {
  const q = useQuery({
    queryKey: qk.onboardingRun(runId),
    queryFn: () => onboardingRuns.get(runId).then((r) => r.data),
    refetchInterval: (query) => runPollInterval(query.state.data?.status),
  });
  // Hooks must run unconditionally (the component returns early below).
  const canWrite = useIsPlatformAdmin();
  // Track readiness BY the diff sha PreviewDiffTable reported, so a previous
  // `true` can't briefly apply to a different run/artifact after a switch.
  const [diffReadySha, setDiffReadySha] = useState<string | null>(null);

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading run…</p>;
  if (q.error) return <ErrorBanner error={q.error} title="Failed to load run" />;
  const run = q.data;
  if (!run) return null;
  const binding = run.approval_binding;
  const diffArtifact = run.artifacts.find((a) => a.name === "preview-diff.json") ?? null;
  const diffReady = diffArtifact != null && diffReadySha === diffArtifact.content_sha256;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <section style={{ display: "grid", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: "1rem" }}>{run.run_type}</h3>
          <StatusBadge status={run.status} />
        </div>
        <div style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>
          scope <code>{run.lock_scope}:{run.lock_key}</code> · tenant <code>{run.tenant_id}</code>
          {run.site_id && <> · site <code>{run.site_id}</code></>} · created {new Date(run.created_at).toLocaleString()}
          {run.approved_by && <> · approved by {run.approved_by}{run.approved_at && ` (${new Date(run.approved_at).toLocaleString()})`}</>}
        </div>
        <div style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>
          {run.execution_kind === "api" ? (
            <>executor ref <code>{run.executor_ref ?? "—"}</code></>
          ) : (
            <>playbook commit <code>{run.playbook_commit ?? "—"}</code></>
          )}
          {run.status_reason && <> · reason <code>{run.status_reason}</code></>}
        </div>
      </section>

      {LEASE_STATES.has(run.status) && run.claimed_by && (
        <section style={{ fontSize: "0.82rem" }}>
          <strong>Runner:</strong> {run.claimed_by}
          {run.lease_expires_at && <> · {formatLeaseCountdown(run.lease_expires_at)}</>}
          {run.heartbeat_at && <> · last heartbeat {new Date(run.heartbeat_at).toLocaleString()}</>}
        </section>
      )}

      {binding && (
        <section>
          <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>What was approved</h4>
          <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", fontSize: "0.8rem" }}>
            {Object.entries({ ...binding, diff_version: run.diff_version }).map(([k, v]) => (
              <div key={k} style={{ display: "contents" }}>
                <dt style={{ color: "var(--text-dim)" }}>{k}</dt>
                <dd style={{ margin: 0 }}><code>{String(v)}</code></dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {diffArtifact && (
        <section>
          <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>Preview diff (approval evidence)</h4>
          <PreviewDiffTable sha={diffArtifact.content_sha256} onReadyChange={setDiffReadySha} />
        </section>
      )}

      {run.status === "failed" && run.status_reason === "executor_version_changed" && (
        <section style={{ color: "var(--crit)", fontSize: "0.85rem" }}>
          The deployed Hub version changed since this run was approved, so it was not executed.
          Create a fresh run from the same render under the current build.
        </section>
      )}

      <RunActions runId={run.id} status={run.status} executionKind={run.execution_kind}
        canWrite={canWrite} diffReady={diffReady} />

      <section>
        <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>Artifacts</h4>
        {run.artifacts.length === 0 ? (
          <p style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>No artifacts pinned.</p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {run.artifacts.map((a) => <ArtifactDrawer key={a.content_sha256 + a.name} artifact={a} />)}
          </div>
        )}
      </section>

      <section>
        <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>Steps</h4>
        <StepsTimeline steps={run.steps} />
      </section>
    </div>
  );
}
